import { css } from '@emotion/react'
import styled from '@emotion/styled'
import { AnimatedTitle } from 'app/components/Common/Animation/AnimatedTitle'
import { DropDown } from 'app/components/Common/Animation/DropDown'
import { FadeInUp } from 'app/components/Common/Animation/FadeInUp'
import { Image, Props as ImageProps } from 'app/components/Common/Image'
import { FlexBox } from 'app/components/Layout/FlexBox'
import { styles, theme } from 'app/theme'
import React, { memo } from 'react'
import { Parallax } from 'react-scroll-parallax'

export interface Props {
  description?: string
  image?: ImageProps
  subtitle?: string
  title?: string
  variant?: Variant
}

export const Intro = memo(function Intro({
  description,
  image,
  subtitle,
  title,
  variant = 'default',
}: Props) {
  if (!title) {
    return null
  }

  return (
    <Container tag="section" dial={4} row space="between" variant={variant}>
      {image ? (
        <Wrapper className="item-wrapper">
          <DropDown>
            <Parallax scale={[1.4, 1]} translateY={['-20%', '20%']}>
              <Image {...image} />
            </Parallax>
          </DropDown>
        </Wrapper>
      ) : null}

      {title ? (
        <Text className="item-text">
          <Title tag="h1" text={title} className="item-title" />

          {subtitle ? (
            <FadeInUp delay={1}>
              <Subtitle>{subtitle}</Subtitle>
            </FadeInUp>
          ) : null}

          {description ? (
            <FadeInUp delay={1}>
              <Description
                className="item-desc"
                dangerouslySetInnerHTML={{ __html: description }}
              />
            </FadeInUp>
          ) : null}
        </Text>
      ) : null}
    </Container>
  )
})

const Container = styled(FlexBox)<ContainerProps>`
  ${({ variant }) => {
    switch (variant) {
      case 'default':
        return Style
      case 'column':
        return Column
      case 'light':
        return Light
      case 'transparent':
        return Transparent
    }
  }};

  @media (max-width: 1199px) {
    display: flex;
    flex-direction: column-reverse;
  }
`

const Text = styled.div`
  max-width: 32.875rem;

  @media (max-width: 1199px) {
    max-width: none;
  }
`

const Title = styled(AnimatedTitle)`
  ${styles.title.normal}
`

const Subtitle = styled.div`
  ${styles.title.small}

  text-transform: none;
`

const Description = styled.div`
  ${styles.description.light}

  margin: 3rem auto 0;

  @media (max-width: 1199px) {
    margin: 1.5rem auto 0;
  }
`

const Wrapper = styled.div`
  position: relative;
  overflow: hidden;
  width: 34.722vw;
  aspect-ratio: 1.275;

  > div {
    height: 100%;
  }

  img {
    ${styles.img}
  }

  @media (max-width: 1199px) {
    width: 100%;
    margin-top: 3rem;
    aspect-ratio: 1.333;
  }
`

const Style = css`
  position: relative;
  width: 100%;
  display: flex;
  align-items: center;
  padding: 0 13.472vw 0 5.833vw;
  margin: 6rem auto 7.5rem;

  @media (min-width: 1200px) {
    .item-wrapper {
      margin-right: 9.306vw;
    }
  }

  @media (max-width: 1199px) {
    padding: 0 3rem;
    margin: 3rem auto 5.25rem;
  }
`

const Column = css`
  position: relative;
  width: 100%;
  display: flex;
  align-items: center;
  flex-direction: column-reverse;
  padding: 0 13.472vw;
  margin: 6rem auto 7.5rem;

  .item-wrapper {
    width: 100%;
    margin-top: 4.5rem;
    aspect-ratio: 1.9;

    @media (max-width: 1199px) {
      aspect-ratio: 1.333;
      margin-top: 3rem;
    }
  }

  .item-text {
    max-width: 44.25rem;
    text-align: center;

    @media (max-width: 1199px) {
      max-width: none;
    }
  }

  @media (max-width: 1199px) {
    padding: 0 3rem;
    margin: 3rem auto 5.25rem;
  }
`

const Light = css`
  position: relative;
  width: 100%;
  display: flex;
  align-items: center;
  flex-direction: column-reverse;
  padding: 0 13.472vw;
  margin: 6rem auto 4.5rem;

  .item-wrapper {
    width: 100%;
    margin-top: 4.5rem;
    aspect-ratio: 1.9;

    @media (max-width: 1199px) {
      aspect-ratio: 1.333;
    }
  }

  .item-text {
    max-width: 44.25rem;
    text-align: center;
    color: ${theme.colors.variants.neutralDark2};

    @media (max-width: 1199px) {
      max-width: none;
    }
  }

  ${Title},
  ${Subtitle},
  ${Description} {
    color: ${theme.colors.variants.neutralDark2};
  }

  @media (max-width: 1199px) {
    padding: 0 3rem;
    margin: 3rem auto;
  }
`

const Transparent = css`
  ${Light}

  padding-top: 9.5625rem;
  margin: 0 auto;

  .item-text {
    max-width: 32.875rem;
  }

  ${Title},
  ${Subtitle},
  ${Description} {
    color: ${theme.colors.variants.neutralDark2};
  }

  ${Description} {
    margin-top: 2.25rem;
  }

  @media (max-width: 1199px) {
    padding: 7.5rem 3rem 0;
    margin: 0 auto;
  }
`

interface ContainerProps {
  variant: Variant
}

export type Variant = 'default' | 'column' | 'light' | 'transparent'
